import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SOCKET_TOPICS, ORDER_STATUS } from 'src/common/constants';
import { errorHandler } from 'src/common/helpers';
import { API_ENDPOINTS } from 'src/setup/client/api-endpoints';
import HttpClient from 'src/setup/client/http-client';
import {
    SpotAsset,
    TokenInfo,
    Toast,
    ActiveOrder,
    ConditionalOrder,
    TradeOrder,
    MyTrade,
    SpotOrderSetting,
    Symbol,
    SpotOrderPayload,
    TokenInfoPayload,
    SpotOrderSettingPayload,
    GetSpotLimitOrMarketOrderPayload,
    GetSpotConditionalOrderPayload,
    SpotAllActiveOrderCancelPayload,
    SpotAllConditionalOrderCancelPayload,
    SpotBalance,
    SpotConditionalOrderCancelPayload,
    SpotActiveOrderCancelPayload,
} from 'src/types';

interface SpotState {
    loading: Record<string, boolean>;
    error: Record<string, any>;
    currentSymbol: Symbol;
    assets: SpotAsset[];
    orderCreated: number;
    createOrderPrice: string;
    tvWidgetRef: any;
    tokenInfo: TokenInfo;
    toast: Toast;
    activeOpenOrders: ActiveOrder[];
    conditionalOpenOrders: ConditionalOrder[];
    activeTradeOrders: TradeOrder[];
    conditionalTradeOrders: TradeOrder[];
    tradeOrderHistory: TradeOrder[];
    myTrades: MyTrade[];
    orderSetting: SpotOrderSetting;
}

const initialState: SpotState = {
    loading: {},
    error: {},
    currentSymbol: {} as Symbol,
    assets: [] as SpotAsset[],
    createOrderPrice: '',
    tvWidgetRef: null,
    tokenInfo: {} as TokenInfo,
    toast: {} as Toast,
    activeOpenOrders: [] as ActiveOrder[],
    conditionalOpenOrders: [] as ConditionalOrder[],
    activeTradeOrders: [] as TradeOrder[],
    conditionalTradeOrders: [] as TradeOrder[],
    tradeOrderHistory: [] as TradeOrder[],
    myTrades: [] as MyTrade[],
    orderSetting: {
        isConfirm: true,
    },
    orderCreated: 0,
};

// Async thunk actions and status keys
export const AsyncActionKeys = {
    createOrder: 'createOrder',
    fetchActiveOpenOrders: 'fetchActiveOpenOrders',
    fetchConditionalOpenOrders: 'fetchConditionalOpenOrders',
    fetchTokenInfo: 'fetchTokenInfo',
    fetchAvailableAssets: 'fetchAvailableAssets',
    fetchActiveTradeOrders: 'fetchActiveTradeOrders',
    fetchConditionalTradeOrders: 'fetchConditionalTradeOrders',
    fetchMyTrades: 'fetchMyTrades',
    cancelActiveOrder: 'cancelActiveOrder',
    cancelConditionalOrder: 'cancelConditionalOrder',
    fetchOrderSetting: 'fetchOrderSetting',
    setOrderSetting: 'setOrderSetting',
    getLimitOrMarketOrderDetail: 'getLimitOrMarketOrderDetail',
    getConditionalOrderDetail: 'getConditionalOrderDetail',
    cancelAllActiveOrders: 'cancelAllActiveOrders',
    cancelAllConditionalOrders: 'cancelAllConditionalOrders',
};

export const createOrder = createAsyncThunk(
    AsyncActionKeys.createOrder,
    async (body: SpotOrderPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${
                    body.trigger_price
                        ? API_ENDPOINTS.CREATE_SPOT_CONDITIONAL_ORDER
                        : API_ENDPOINTS.CREATE_SPOT_ORDER
                }`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchActiveOpenOrders = createAsyncThunk(
    AsyncActionKeys.fetchActiveOpenOrders,
    async (queryParams: String, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${API_ENDPOINTS.SPOT_ACTIVE_OPEN_ORDERS}?${queryParams}`,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchConditionalOpenOrders = createAsyncThunk(
    AsyncActionKeys.fetchConditionalOpenOrders,
    async (queryParams: String, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${API_ENDPOINTS.SPOT_CONDITIONAL_OPEN_ORDERS}?${queryParams}`,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchTokenInfo = createAsyncThunk(
    AsyncActionKeys.fetchTokenInfo,
    async (body: TokenInfoPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.TOKEN_INFO}`, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchAvailableAssets = createAsyncThunk(
    AsyncActionKeys.fetchAvailableAssets,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.get(`${API_ENDPOINTS.AVAILABLE_SPOT_ASSETS}`);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchActiveTradeOrders = createAsyncThunk(
    AsyncActionKeys.fetchActiveTradeOrders,
    async (queryParams: string, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${API_ENDPOINTS.SPOT_ACTIVE_TRADE_ORDERS}?${queryParams}`,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchConditionalTradeOrders = createAsyncThunk(
    AsyncActionKeys.fetchConditionalTradeOrders,
    async (queryParams: string, thunkAPI) => {
        try {
            const response = await HttpClient.get(
                `${API_ENDPOINTS.SPOT_CONDITIONAL_TRADE_ORDERS}?${queryParams}`,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchMyTrades = createAsyncThunk(
    AsyncActionKeys.fetchMyTrades,
    async (queryParams: string, thunkAPI) => {
        try {
            const response = await HttpClient.get(`${API_ENDPOINTS.SPOT_MY_TRADES}?${queryParams}`);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const cancelActiveOrder = createAsyncThunk(
    AsyncActionKeys.cancelActiveOrder,
    async (body: SpotActiveOrderCancelPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.SPOT_ACTIVE_ORDER_CANCEL}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const cancelConditionalOrder = createAsyncThunk(
    AsyncActionKeys.cancelConditionalOrder,
    async (body: SpotConditionalOrderCancelPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.SPOT_CONDITIONAL_ORDER_CANCEL}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const fetchOrderSetting = createAsyncThunk(
    AsyncActionKeys.fetchOrderSetting,
    async (_, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.GET_SPOT_ORDER_SETTING}`, {});
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const setOrderSetting = createAsyncThunk(
    AsyncActionKeys.setOrderSetting,
    async (body: SpotOrderSettingPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(`${API_ENDPOINTS.SET_SPOT_ORDER_SETTING}`, body);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const getLimitOrMarketOrderDetail = createAsyncThunk(
    AsyncActionKeys.getLimitOrMarketOrderDetail,
    async (body: GetSpotLimitOrMarketOrderPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.GET_SPOT_LIMIT_MARKET_ORDER}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const getConditionalOrderDetail = createAsyncThunk(
    AsyncActionKeys.getConditionalOrderDetail,
    async (body: GetSpotConditionalOrderPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.GET_SPOT_CONDITIONAL_ORDER}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const cancelAllActiveOrders = createAsyncThunk(
    AsyncActionKeys.cancelAllActiveOrders,
    async (body: SpotAllActiveOrderCancelPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.CANCEL_ALL_SPOT_LIMIT_ORDERS}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

export const cancelAllConditionalOrders = createAsyncThunk(
    AsyncActionKeys.cancelAllConditionalOrders,
    async (body: SpotAllConditionalOrderCancelPayload, thunkAPI) => {
        try {
            const response = await HttpClient.post(
                `${API_ENDPOINTS.CANCEL_ALL_SPOT_CONDITIONAL_ORDERS}`,
                body,
            );
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    },
);

//helper to filter orders
function filterOrders(order: ActiveOrder | TradeOrder, orders: TradeOrder[] | ActiveOrder[]) {
    const orderExist = orders.some((currentOrder) => currentOrder.orderId === order.orderId);
    if (orderExist) {
        return orders.map((currentOrder) => {
            if (currentOrder.orderId === order.orderId) {
                return order;
            }
            return currentOrder;
        });
    } else {
        return [order as TradeOrder, ...orders];
    }
}

//slice
export const spotSlice = createSlice({
    name: 'spotSlice',
    initialState,
    reducers: {
        showNotification: (state, action) => {
            state.toast = {
                id: new Date().getTime().toString(),
                title: action.payload.title,
                type: action.payload.type,
                description: action.payload.description && action.payload.description,
            };
        },
        saveCurrentSymbol: (state, action: PayloadAction<Symbol>) => {
            state.currentSymbol = action.payload;
            localStorage.setItem('currentSpotSymbol', action.payload.symbolId);
        },
        updateTvWidgetRef: (state, action) => {
            state.tvWidgetRef = action.payload;
        },
        updateCreateOrderPrice: (state, action) => {
            state.createOrderPrice = action.payload;
        },
        updateUserTradeData: (state, action) => {
            const { data, topic } = action.payload;
            if (data) {
                switch (topic) {
                    case SOCKET_TOPICS.ORDER: // limit orders
                        data.map((order: ActiveOrder) => {
                            if (
                                order.status === ORDER_STATUS.NEW ||
                                order.status === ORDER_STATUS.PARTIALLY_FILLED
                            ) {
                                const orders = filterOrders(order, state.activeOpenOrders);
                                state.activeOpenOrders = orders as ActiveOrder[];
                            } else if (
                                order.status === ORDER_STATUS.FILLED ||
                                order.status === ORDER_STATUS.CANCELED
                            ) {
                                state.activeOpenOrders = state.activeOpenOrders.filter(
                                    (currentOrder) => currentOrder.orderId !== order.orderId,
                                );
                            }
                            state.tradeOrderHistory = filterOrders(order, state.tradeOrderHistory);
                            state.activeOpenOrders.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                            state.tradeOrderHistory.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                            if (
                                order.status === ORDER_STATUS.PARTIALLY_FILLED ||
                                order.status === ORDER_STATUS.FILLED
                            ) {
                                state.toast = {
                                    id: order.clientOrderId,
                                    title:
                                        order.status === ORDER_STATUS.FILLED
                                            ? 'order_filled'
                                            : 'order_partially_filled',
                                    type: 'info',
                                };
                            }
                        });

                        break;
                    case SOCKET_TOPICS.PLAN_ORDER: // conditional orders
                        data.map((order: ConditionalOrder) => {
                            if (order.status === ORDER_STATUS.ORDER_NEW) {
                                const orders = filterOrders(order, state.conditionalOpenOrders);
                                state.conditionalOpenOrders = orders as ConditionalOrder[];
                            } else if (
                                order.status === ORDER_STATUS.ORDER_FILLED ||
                                order.status === ORDER_STATUS.ORDER_CANCELED
                            ) {
                                state.conditionalOpenOrders = state.conditionalOpenOrders.filter(
                                    (currentOrder) => currentOrder.orderId !== order.orderId,
                                );
                            }
                            state.tradeOrderHistory = filterOrders(order, state.tradeOrderHistory);
                            state.conditionalOpenOrders.sort((a, b) =>
                                a.time - b.time > 0 ? -1 : 1,
                            );
                            state.tradeOrderHistory.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                            if (
                                order.status === ORDER_STATUS.PARTIALLY_FILLED ||
                                order.status === ORDER_STATUS.FILLED
                            ) {
                                state.toast = {
                                    id: order.clientOrderId,
                                    title:
                                        order.status === ORDER_STATUS.FILLED
                                            ? 'order_filled'
                                            : 'order_partially_filled',
                                    type: 'info',
                                };
                            }
                        });
                        break;
                    case SOCKET_TOPICS.BALANCE:
                        state.assets = state.assets.map((asset) => {
                            const balance = data.find(
                                (balance: SpotBalance) => balance.tokenId === asset.tokenId,
                            );
                            return balance ? { ...asset, ...balance } : asset;
                        });
                        break;
                    default:
                        break;
                }
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(createOrder.fulfilled, (state) => {
                state.orderCreated = ++state.orderCreated;
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'order_successful',
                    type: 'success',
                };
            })
            .addCase(createOrder.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(fetchActiveOpenOrders.fulfilled, (state, action) => {
                state.activeOpenOrders = action.payload;
            })
            .addCase(fetchConditionalOpenOrders.fulfilled, (state, action) => {
                state.conditionalOpenOrders = action.payload;
            })
            .addCase(fetchTokenInfo.fulfilled, (state, action) => {
                state.tokenInfo = action.payload;
            })
            .addCase(fetchAvailableAssets.fulfilled, (state, action) => {
                state.assets = action.payload;
            })
            .addCase(fetchActiveTradeOrders.fulfilled, (state, action) => {
                state.activeTradeOrders = action.payload;
                state.tradeOrderHistory = [
                    ...state.activeTradeOrders,
                    ...state.conditionalTradeOrders,
                ].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
            })
            .addCase(fetchConditionalTradeOrders.fulfilled, (state, action) => {
                state.conditionalTradeOrders = action.payload;
                state.tradeOrderHistory = [
                    ...state.activeTradeOrders,
                    ...state.conditionalTradeOrders,
                ].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
            })
            .addCase(fetchMyTrades.fulfilled, (state, action) => {
                state.myTrades = action.payload;
            })
            .addCase(cancelActiveOrder.fulfilled, (state, action) => {
                const { order_id } = action.meta.arg;
                const canceledOrder = { ...action.payload, orderId: order_id };
                if (canceledOrder.success) {
                    state.activeOpenOrders = state.activeOpenOrders.filter(
                        (order: ActiveOrder) => order.orderId !== canceledOrder.orderId,
                    );
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'order_cancelled',
                        type: 'success',
                    };
                } else {
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'order_cancelled_failure',
                        type: 'error',
                    };
                }
            })
            .addCase(cancelActiveOrder.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(cancelConditionalOrder.fulfilled, (state, action) => {
                const { order_id } = action.meta.arg;
                const canceledOrder = { ...action.payload, orderId: order_id };
                if (canceledOrder.success) {
                    state.conditionalOpenOrders = state.conditionalOpenOrders.filter(
                        (order: ConditionalOrder) => order.orderId !== canceledOrder.orderId,
                    );
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'order_cancelled',
                        type: 'success',
                    };
                } else {
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'order_cancelled_failure',
                        type: 'error',
                    };
                }
            })
            .addCase(cancelConditionalOrder.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(fetchOrderSetting.fulfilled, (state, action) => {
                state.orderSetting = action.payload;
            })
            .addCase(setOrderSetting.fulfilled, (state, action) => {
                const { is_confirm } = action.meta.arg;
                const response = { isConfirm: is_confirm };
                state.orderSetting = response;
            })
            .addCase(setOrderSetting.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(cancelAllActiveOrders.fulfilled, (state, action) => {
                const { symbol_ids } = action.meta.arg;
                const response = { ...action.payload, symbolId: symbol_ids ? symbol_ids : 'ALL' };
                if (response.success) {
                    state.activeOpenOrders = state.activeOpenOrders.filter(
                        (order: ActiveOrder) => order.symbolId !== response.symbolId,
                    );
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'orders_cancelled',
                        type: 'success',
                    };
                } else {
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'orders_cancelled_failure',
                        type: 'error',
                    };
                }
            })
            .addCase(cancelAllActiveOrders.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            })
            .addCase(cancelAllConditionalOrders.fulfilled, (state, action) => {
                const { symbol_id } = action.meta.arg;
                const response = { ...action.payload, symbolId: symbol_id ? symbol_id : 'ALL' };
                if (response.success) {
                    state.conditionalOpenOrders = state.conditionalOpenOrders.filter(
                        (order: ConditionalOrder) => order.symbolId !== response.symbolId,
                    );
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'orders_cancelled',
                        type: 'success',
                    };
                } else {
                    state.toast = {
                        id: new Date().getTime().toString(),
                        title: 'orders_cancelled_failure',
                        type: 'error',
                    };
                }
            })
            .addCase(cancelAllConditionalOrders.rejected, (state, action) => {
                const error = errorHandler(action.payload);
                state.toast = error;
            });

        // Loading and error handling for async actions
        builder.addMatcher(
            (action) => action.type.endsWith('/pending'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = true;
            },
        );
        builder.addMatcher(
            (action) => action.type.endsWith('/fulfilled'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = false;
                state.error[actionType] = undefined;
            },
        );
        builder.addMatcher(
            (action) => action.type.endsWith('/rejected'),
            (state, action) => {
                const actionType = action.type.split('/')[0];

                state.loading[actionType] = false;
                state.error[actionType] = action.payload;
            },
        );
    },
});

export const {
    showNotification,
    updateCreateOrderPrice,
    updateTvWidgetRef,
    updateUserTradeData,
    saveCurrentSymbol,
} = spotSlice.actions;
